/**
 * Operations Overview / Decision Inbox — the agentic surface's landing page.
 *
 * Read-only render of GET /api/agentic/overview (immutable agent_decisions
 * ledger aggregates + supervised-decision inbox state + safe mode + pending
 * dual-control approvals). On 403/404 from the entitlement gate it renders
 * the NotEnabled state — never a crash or blank screen.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Inbox, ShieldAlert, ShieldCheck } from 'lucide-react';
import {
  Badge,
  Card,
  EmptyState,
  Heading,
  Icon,
  KpiCard,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
  Button,
} from '@smms/design-system';
import { getOverview, type RecentDecision } from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';

const RISK_BADGE: Record<RecentDecision['risk_classification'], 'success' | 'warning' | 'danger'> =
  {
    routine: 'success',
    non_routine: 'warning',
    safety_critical: 'danger',
  };

const RISK_LABEL: Record<RecentDecision['risk_classification'], string> = {
  routine: 'Routine',
  non_routine: 'Non-routine',
  safety_critical: 'Safety-critical',
};

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function OverviewPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agentic', 'overview'],
    queryFn: getOverview,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="overview-loading">
        <Spinner size="lg" label="Loading Agentic Operations" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load Agentic Operations"
          description="Something went wrong while loading the overview. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  const { decisions, safe_mode, config_approvals, recent_decisions } = data;

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <Stack gap="lg">
        <PageHeader
          title="Operations Overview"
          description="Read-only view of agent decisions, supervision state, and platform controls for your tenant."
          actions={
            safe_mode.active ? (
              <Badge variant="danger" dot>
                Safe Mode active
              </Badge>
            ) : (
              <Badge variant="success" dot>
                Operating normally
              </Badge>
            )
          }
        />

        <Section>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard label="Decisions (all time)" value={decisions.total} />
            <KpiCard label="Decisions (24h)" value={decisions.last_24h} />
            <KpiCard label="Open supervised" value={decisions.open_supervised} />
            <KpiCard label="Pending approvals" value={config_approvals.pending} />
          </div>
        </Section>

        <Section>
          <Card padding="md">
            <Stack gap="sm">
              <div className="flex items-center gap-2">
                <Icon
                  as={safe_mode.active ? ShieldAlert : ShieldCheck}
                  size="sm"
                  className={safe_mode.active ? 'text-danger' : 'text-success'}
                />
                <Heading level={2} className="text-base">
                  Safe Mode
                </Heading>
              </div>
              {safe_mode.active ? (
                <Text variant="body-sm" tone="danger">
                  Safe Mode was entered {formatWhen(safe_mode.entered_at)}
                  {safe_mode.reason ? ` — ${safe_mode.reason}` : ''}. Agent actions are halted;
                  exiting requires dual-control approval.
                </Text>
              ) : (
                <Text variant="body-sm" tone="muted">
                  Safe Mode is not active. Agent decisions by risk:{' '}
                  {RISK_LABEL.routine.toLowerCase()} {decisions.by_risk.routine},{' '}
                  {RISK_LABEL.non_routine.toLowerCase()} {decisions.by_risk.non_routine},{' '}
                  {RISK_LABEL.safety_critical.toLowerCase()} {decisions.by_risk.safety_critical}
                  {decisions.escalated > 0 ? ` — ${decisions.escalated} escalated` : ''}.
                </Text>
              )}
            </Stack>
          </Card>
        </Section>

        <Section>
          <Stack gap="sm">
            <Heading level={2} className="text-base">
              Decision Inbox
            </Heading>
            {recent_decisions.length === 0 ? (
              <Card padding="lg">
                <EmptyState
                  compact
                  icon={<Icon as={Inbox} size="lg" />}
                  title="No agent decisions yet"
                  description="When agents start recording decisions for your tenant, the most recent ones appear here."
                />
              </Card>
            ) : (
              <Card padding="none">
                <ul className="divide-y divide-border" data-testid="decision-inbox">
                  {recent_decisions.map(d => (
                    <li key={d.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <Text variant="body-sm" className="font-medium">
                          {d.decision_type.replaceAll('_', ' ')}
                        </Text>
                        <Text variant="caption" tone="muted">
                          {d.agent_id.replaceAll('_', ' ')} · {d.autonomy_phase} ·{' '}
                          {formatWhen(d.created_at)}
                        </Text>
                      </div>
                      <Badge variant={RISK_BADGE[d.risk_classification]} size="sm">
                        {RISK_LABEL[d.risk_classification]}
                      </Badge>
                      {d.latest_response_type && (
                        <Badge variant="info" size="sm">
                          {d.latest_response_type.replaceAll('_', ' ')}
                        </Badge>
                      )}
                      {d.deadline_state && d.deadline_state !== 'closed' && (
                        <Badge variant="warning" size="sm">
                          {d.deadline_state.replaceAll('_', ' ')}
                        </Badge>
                      )}
                      <Text variant="mono" tone="muted">
                        {d.calibrated_confidence ?? d.raw_confidence}%
                      </Text>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </Stack>
        </Section>
      </Stack>
    </div>
  );
}
